import './booking-page.css';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { OfflineBookingPage } from './components/offline-booking-page';
import { OnlineBookingPage } from './components/online-booking-page';
import {
  OpportunityComplete,
  CaseInfo,
  CaseStatus,
  Result,
  Slot,
  defaultCaseInfo,
  CallBodyRequest,
  OpportunityAssignDto,
  PreSalesApiResponse,
  PreSalesAutodialerHpInfo,
  PreSalesQuestion,
  PreSalesQuestionnaireContext,
} from '../../dto/model';
import { useSharedLoadingGuard } from '../../hooks/use-loading-guard';
import { GetAccessToken, HasRole, Role } from '../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { bookingPageService } from '../../services/booking-page-service';
import { heatBookingPageService } from '../../services/heat-booking-page-service';
import { Header } from '../header/header';
import { HeaderBookingPage } from '../header/components/header-booking-page';
import { OnlineBookingNoCasesPage } from './components/online-booking-no-cases-page';
import { Alert, App, Button, Spin } from 'antd';
import { strings } from '../../lang';
import { ConfirmationPage } from './components/confirmation-page';
import CaseScript from './components/case-script';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { OnlineRecordingBookingPage } from './components/online-recording-booking-page';
import { salesAppointmentsService } from '../../services/sales-appointments-service';
import { useSalesChannelState } from '../../store/header-state';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/shallow';
import { PowerHourConfig, UserDataDto } from '../../dto/reporting-models';
import { reportingService } from '../../services/reporting-service';
import PowerHourBar from '../power-hour/power-hour-bar';
import { handleBeforeUnload } from '../../utils/event-utils';
import { wait } from '../../services/utils';
import { mapPreSalesCustomerInfoToCaseInfo, resolveHpDealId } from './autodialer-hp-utils';
import { useCallRecording } from '../../hooks/use-call-recording';
import { redirectUnauthorizedAutodialerHpToPresalesTool } from './autodialer-hp-presales-tool-redirect';
import { AssignmentConflictModal } from './modal/assignment-conflict-modal';
import { buildCaseComplete } from './case-complete-utils';
import { fireBookingConfetti } from './booking-confetti';
import { ASSIGNMENT_CHANGE_CHANNEL, AssignmentChangedMessage, isAssignmentChangedMessage } from './assignment-change-channel';
import { AssignmentChangedModal } from './modal/assignment-changed-modal';

const AdBookingPage = lazy(() => import('./ad-booking-page').then((module) => ({ default: module.AdBookingPage })));

/** Dedupes parallel autodialer inits (e.g. React Strict Mode double-mount). */
const autodialerInitByLinkId = new Map<string, Promise<boolean>>();
const autodialerHpInitByCrmId = new Map<string, Promise<PreSalesApiResponse<PreSalesAutodialerHpInfo>>>();
type AutodialerHpInitializationOutcome = 'ready' | 'failed' | 'redirecting';

export const BookingPage = () => {
  const [params] = useSearchParams();
  return params.get('type') === 'ad'
    ? <Suspense fallback={<Spin size="large" />}><AdBookingPage /></Suspense>
    : <BookingPageCore />;
};

const BookingPageCore = () => {
  const { notification } = App.useApp();
  const [caseInfo, setCaseInfo] = useState<CaseInfo | null>(defaultCaseInfo);
  const [cancelledSlot, setCancelledSlot] = useState<Slot | null>(null);
  const [error, setError] = useState<any>();
  /** Set when an autodialer link is opened while the agent still holds a different active assignment. */
  const [conflictOldCase, setConflictOldCase] = useState<CaseInfo | null>(null);
  const [assignmentChanged, setAssignmentChanged] = useState(false);
  const { instance, inProgress, accounts } = useMsal();
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [displayConfirmationPage, setDisplayConfirmationPage] = useState<boolean>(false);
  const [bookedSlot, setBookedSlot] = useState<Slot>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [retryCountdown, setRetryCountdown] = useState<number>();
  const [inputCaseComments, setInputCaseComments] = useState<string>('');
  const [lastPersistedComment, setLastPersistedComment] = useState<string>('');
  const salesChannel = useSalesChannelState(useShallow((state) => caseInfo?.salesChannel ?? state.salesChannel));
  const [bookingButtonStyle, setBookingButtonStyle] = useState<string | undefined>(undefined);
  const [activePowerHourConfig, setActivePowerHourConfig] = useState<PowerHourConfig | null>(null);
  const [callId, setCallId] = useState<string>('');
  const [isOpeningNextCase, setIsOpeningNextCase] = useState(false);
  const [preSalesQuestionnaire, setPreSalesQuestionnaire] = useState<PreSalesQuestion[]>([]);
  const [preSalesContext, setPreSalesContext] = useState<PreSalesQuestionnaireContext>({});
  const withGuard = useSharedLoadingGuard();
  dayjs.extend(utc);

  const userShouldBeRecorded = HasRole(accounts, Role.Recorded);
  const isTalkDeskAgent = HasRole(accounts, Role.TalkdeskAgent);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingType = searchParams.get('type');
  const isAutodialerHpFlow = bookingType === 'autodialerhp';
  const isAutodialerFlow = bookingType === 'autodialer';
  const isDirectCaseFlow = isAutodialerFlow || isAutodialerHpFlow;
  const autodialerInternalId = searchParams.get('id') ?? '';
  const autodialerHpCrmId = searchParams.get('crmId') ?? '';
  const hideScriptsAndObjections = isAutodialerHpFlow;
  const inboundCenter = location?.state?.inboundCenter;
  const inboundAutodialed = location?.state?.isAutodialed === true || isDirectCaseFlow;
  const navigate = useNavigate();
  const currentCaseIdRef = useRef(caseInfo?.internalId);
  const assignmentChangeChannelRef = useRef<BroadcastChannel | null>(null);
  currentCaseIdRef.current = caseInfo?.internalId;

  const recording = useCallRecording({
    isOnline,
    userShouldBeRecorded,
    isAutodialerFlow,
    salesChannel,
    caseInfo,
    instance,
    inProgress,
  });

  const invalidateCurrentAssignment = () => {
    recording.stopAndDiscard();
    setAssignmentChanged(true);
  };

  useEffect(() => {
    if (!isAutodialerFlow || typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(ASSIGNMENT_CHANGE_CHANNEL);
    assignmentChangeChannelRef.current = channel;
    channel.onmessage = ({ data }: MessageEvent<unknown>) => {
      const pageIsInactive = document.visibilityState === 'hidden' || !document.hasFocus();
      if (pageIsInactive && isAssignmentChangedMessage(data) && data.previousCaseId === currentCaseIdRef.current) {
        invalidateCurrentAssignment();
      }
    };

    return () => {
      channel.close();
      if (assignmentChangeChannelRef.current === channel) assignmentChangeChannelRef.current = null;
    };
  }, [isAutodialerFlow]);

  useEffect(() => {
    const revalidateAssignment = async () => {
      const currentCaseId = currentCaseIdRef.current;
      if (
        !isAutodialerFlow ||
        !currentCaseId ||
        assignmentChanged ||
        displayConfirmationPage ||
        document.visibilityState === 'hidden'
      ) return;

      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const assigned = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
        // Ignore a response started for a case that is no longer displayed.
        if (currentCaseIdRef.current?.toLowerCase() !== currentCaseId.toLowerCase()) return;

        const assignmentMissing =
          !assigned.isSuccess &&
          assigned.errors?.some((error) => /\b404\b/.test(error) || error.includes('ASSIGNMENT_NOT_FOUND')) === true;
        if (assignmentMissing || (assigned.isSuccess && assigned.data.internalId.toLowerCase() !== currentCaseId.toLowerCase())) {
          invalidateCurrentAssignment();
        }
      } catch {
        // A transient authentication or network failure is not proof that the assignment changed.
      }
    };

    window.addEventListener('focus', revalidateAssignment);
    document.addEventListener('visibilitychange', revalidateAssignment);
    return () => {
      window.removeEventListener('focus', revalidateAssignment);
      document.removeEventListener('visibilitychange', revalidateAssignment);
    };
  }, [assignmentChanged, displayConfirmationPage, inProgress, instance, isAutodialerFlow, salesChannel]);

  const handleCallClick = async (phoneNumber: string) => {
    if (isTalkDeskAgent) {
      await withGuard(async () => {
        const accessToken = (await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi)).accessToken;
        // Ensure phone number has German country code
        if (phoneNumber.startsWith('0')) {
          phoneNumber = '+49' + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith('49')) {
            phoneNumber = '+49' + phoneNumber.substring(2);
        } else if (!phoneNumber.startsWith('+49')) {
          phoneNumber = '+49' + phoneNumber;
        }
        const body: CallBodyRequest = {
          customerPhoneNumber: phoneNumber,
          customerId: caseInfo?.globalCustomerId!,
        };
        const startCallResponse = await bookingPageService.startCallAsync(caseInfo?.opportunityId!, body, accessToken);
        setCallId(startCallResponse.callId);
      });
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.location.href = `tel:${phoneNumber}`;
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
  };

  //register the event listener that stops users from refreshing the page if they have data in it
  useEffect(() => {
    if (!inboundCenter && !isDirectCaseFlow) {
      if (isOnline) {
        window.addEventListener('beforeunload', handleBeforeUnload);
      } else {
        // TODO: Investigate why it does not remove the event listener on going offline (isOnline = false)
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [isOnline, inboundCenter, isDirectCaseFlow]);

  // Always call the latest reportOutcome from the unload listeners below without re-subscribing.
  const reportOutcomeRef = useRef(recording.reportOutcome);
  reportOutcomeRef.current = recording.reportOutcome;

  // Autodialer-HP flow: if the tab or browser is closed mid-case, stop the recorder and upload the
  // recording tagged ReachedButNotBooked (same as clicking "Close Case"). If a booking already
  // stopped the recorder, reportOutcome is a no-op, so a booked call is never downgraded. Uploading
  // during unload is best-effort — the browser may not keep the request alive to completion.
  useEffect(() => {
    if (!isAutodialerHpFlow) {
      return;
    }
    const handleUnload = () => reportOutcomeRef.current(CaseStatus.ReachedButNotBooked);
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [isAutodialerHpFlow]);

  useEffect(() => {
    if (inboundCenter) {
      onOnlineChanged(inboundCenter);
    }
  }, [inboundCenter]);

  const goToBookingOffline = async () => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (caseInfo?.internalId) {
        await bookingPageService.releaseCase(caseInfo.internalId, salesChannel!, token.accessToken);
      }
    } catch {
      // Case may already be released after close
    }
    recording.stopAndDiscard();
    recording.reset();
    setDisplayConfirmationPage(false);
    setBookedSlot(undefined);
    setCaseInfo(null);
    setInputCaseComments('');
    setLastPersistedComment('');
    setPreSalesQuestionnaire([]);
    setPreSalesContext({});
    setIsOnline(false);
    setError(undefined);
    navigate('/booking', { replace: true });
  };

  const finishAutodialerSession = async (caseComment?: string) => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    if (caseComment && caseComment !== lastPersistedComment && caseInfo?.internalId) {
      await bookingPageService.addCaseComment(caseInfo.internalId, caseComment, salesChannel, token.accessToken);
    }
    setInputCaseComments('');
    setLastPersistedComment('');
    if (userShouldBeRecorded) {
      recording.reportOutcome(CaseStatus.Booked);
      await wait(1);
    }
    notification.success({
      message: strings.inboundCallBack.success,
      duration: 3,
    });
    await goToBookingOffline();
  };

  const applyAssignedCase = async (assignedCase: CaseInfo) => {
    setIsOnline(true);
    setCaseInfo(assignedCase);
    setInputCaseComments('');
    setLastPersistedComment('');
    setPreSalesContext({});
    if (assignedCase.bookingStatus === 'ReschedulingNeeded') {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const cancelledSlotResponse = await salesAppointmentsService.getLatestSlot(token.accessToken, assignedCase.opportunityId, salesChannel);
      if (cancelledSlotResponse.isSuccess) {
        setCancelledSlot(cancelledSlotResponse.data);
      } else {
        setCancelledSlot(null);
      }
    } else {
      setCancelledSlot(null);
    }
  };

  const matchesAutodialerLinkId = (info: CaseInfo) => {
    const linkId = autodialerInternalId.trim();
    return (info.internalId ?? '').trim() === linkId;
  };

  const formatApiError = (error: unknown): string => {
    if (error === null || error === undefined) return '';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      const record = error as Record<string, unknown>;
      if (record.Message) return String(record.Message);
      if (record.message) return String(record.message);
    }
    return String(error);
  };

  const toErrorList = (errors: unknown): string[] => {
    if (!errors) return [];
    if (Array.isArray(errors)) return errors.map((e) => formatApiError(e));
    return [formatApiError(errors)];
  };

  const toAutodialerAssignErrorMessage = (assignErrors: string[]): string => {
    if (assignErrors.some((e) => e.includes('NOT_IN_AUTODIALER'))) return strings.autodialerNotInAutodialer;
    if (assignErrors.some((e) => e.includes('NOT_AUTODIALED_CASE'))) return strings.autodialerNotAutodialedCase;
    if (assignErrors.some((e) => e.includes('CASE_NOT_FOUND'))) return strings.autodialerCaseNotFound;
    return assignErrors.join(', ') || strings.autodialerAssignFailed;
  };

  const runInitializeAutodialerFlow = async (): Promise<boolean> => {
    if (!autodialerInternalId.trim()) {
      setError(strings.autodialerMissingId);
      return false;
    }

    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

    const tryApplyMatchingAssignedCase = async (): Promise<boolean> => {
      const assignedCaseResult = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
      if (assignedCaseResult.isSuccess && matchesAutodialerLinkId(assignedCaseResult.data)) {
        await applyAssignedCase(assignedCaseResult.data);
        return true;
      }
      return false;
    };

    if (await tryApplyMatchingAssignedCase()) {
      return true;
    }

    // The agent still holds a different active assignment (e.g. a leftover booking-queue or
    // autodialer case). Instead of silently releasing it, surface a conflict modal so the agent
    // decides whether to keep the old customer or switch to the new one (handled outside this flow).
    const existingAssignment = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
    if (existingAssignment.isSuccess && existingAssignment.data?.internalId && !matchesAutodialerLinkId(existingAssignment.data)) {
      setConflictOldCase(existingAssignment.data);
      return false;
    }

    const body: OpportunityAssignDto = { internalId: autodialerInternalId.trim(), isAutodialerFlow: true };
    const assignResult = await bookingPageService.assignInboundOpportunity(body, token.accessToken);
    const assignErrors = toErrorList(assignResult.errors);

    if (assignResult.isSuccess || assignErrors.includes('ASSIGNMENT_EXISTS')) {
      if (await tryApplyMatchingAssignedCase()) {
        return true;
      }
      if (assignErrors.includes('ASSIGNMENT_EXISTS')) {
        // Assignment exists but the assigned case still isn't the link's case: re-read it and let
        // the agent resolve the conflict via the modal.
        const conflicting = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
        if (conflicting.isSuccess && conflicting.data?.internalId && !matchesAutodialerLinkId(conflicting.data)) {
          setConflictOldCase(conflicting.data);
          return false;
        }
        setError(strings.autodialerAssignmentExistsOtherCase);
        return false;
      }
    }

    // Assign may have succeeded but response failed (e.g. duplicate parallel request returned 500).
    if (await tryApplyMatchingAssignedCase()) {
      return true;
    }

    setError(toAutodialerAssignErrorMessage(assignErrors));
    return false;
  };

  const runInitializeAutodialerHpFlow = async (): Promise<AutodialerHpInitializationOutcome> => {
    const crmId = autodialerHpCrmId.trim();
    if (!crmId) {
      setError(strings.autodialerHpMissingCrmId);
      return 'failed';
    }

    let initialization = autodialerHpInitByCrmId.get(crmId);
    if (!initialization) {
      initialization = (async () => {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        return heatBookingPageService.getPreSalesAutodialerHpInfo(crmId, token.accessToken);
      })().finally(() => {
        autodialerHpInitByCrmId.delete(crmId);
      });
      autodialerHpInitByCrmId.set(crmId, initialization);
    }

    const result = await initialization;
    if (!result.success || !result.data) {
      const redirectedToPresalesTool = redirectUnauthorizedAutodialerHpToPresalesTool(
        bookingType,
        crmId,
        result.httpStatusCode,
        process.env.REACT_APP_PRESALES_TOOL_BASE_URL,
      );
      if (redirectedToPresalesTool) {
        return 'redirecting';
      }

      if (result.httpStatusCode === 403) {
        setError(strings.autodialerHpForbidden);
      } else {
        setError(result.error ?? result.message ?? strings.autodialerHpLoadFailed);
      }
      return 'failed';
    }

    const customerInfo = result.data.customerInfo;
    const dealId = resolveHpDealId(customerInfo, crmId);
    const hpOrderNumber = customerInfo.hpOrderNumber;

    setIsOnline(true);
    setCaseInfo(mapPreSalesCustomerInfoToCaseInfo(customerInfo, crmId, dealId));
    setCancelledSlot(null);
    setPreSalesQuestionnaire(result.data.questionnaire ?? []);
    setPreSalesContext({
      crmId: dealId,
      hpOrderNumber,
      signupId: customerInfo.signupId,
      gcid: customerInfo.gcid,
      customerSource: customerInfo.customerSource,
    });
    setInputCaseComments('');
    setLastPersistedComment('');
    setError(undefined);
    return 'ready';
  };

  const initializeAutodialerFlow = (): Promise<boolean> => {
    const cacheKey = autodialerInternalId.trim().toLowerCase();
    const existing = autodialerInitByLinkId.get(cacheKey);
    if (existing) {
      return existing;
    }

    const promise = runInitializeAutodialerFlow().catch((err) => {
      autodialerInitByLinkId.delete(cacheKey);
      throw err;
    });
    autodialerInitByLinkId.set(cacheKey, promise);
    return promise;
  };

  /** Conflict modal: keep the existing assignment and replace the link with the old customer. */
  const onConflictProceedWithOld = async () => {
    const oldCase = conflictOldCase;
    if (!oldCase) return;
    await applyAssignedCase(oldCase);
    setConflictOldCase(null);
    setSearchParams(
      { type: 'autodialer', id: oldCase.internalId },
      { replace: true },
    );
  };

  /** Conflict modal: release + complete the old customer as NotReached, then assign the link's customer. */
  const onConflictChooseNew = async () => {
    const oldCase = conflictOldCase;
    if (!oldCase) return;
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

    // A single closeCase(NotReached) both completes the old case (with NotReached analytics) and
    // removes its assignment, freeing the agent to be assigned to the new customer.
    const complete = buildCaseComplete(oldCase, { caseStatus: CaseStatus.NotReached });
    complete.callSource = 'Autodialer';
    const closeResult = await bookingPageService.closeCase(complete, token.accessToken);
    if (!closeResult.isSuccess) {
      notification.error({
        message: strings.errorOccurred,
        description: strings.assignmentConflict.completeOldFailed,
        placement: 'topRight',
      });
      return;
    }

    const body: OpportunityAssignDto = { internalId: autodialerInternalId.trim(), isAutodialerFlow: true };
    const assignResult = await bookingPageService.assignInboundOpportunity(body, token.accessToken);
    const assignErrors = toErrorList(assignResult.errors);

    if (assignResult.isSuccess || assignErrors.includes('ASSIGNMENT_EXISTS')) {
      const assigned = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
      if (assigned.isSuccess && matchesAutodialerLinkId(assigned.data)) {
        await applyAssignedCase(assigned.data);
        setConflictOldCase(null);
        if (assignmentChangeChannelRef.current) {
          const message: AssignmentChangedMessage = { type: 'assignment-changed', previousCaseId: oldCase.internalId };
          assignmentChangeChannelRef.current.postMessage(message);
        }
        notification.success({ message: strings.assignmentConflict.oldCompletedNotReached, placement: 'topRight' });
        return;
      }
    }

    // Old case was released as NotReached but the new customer couldn't be assigned.
    setConflictOldCase(null);
    setError(toAutodialerAssignErrorMessage(assignErrors));
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (isAutodialerFlow) {
        try {
          await initializeAutodialerFlow();
        } catch (e: any) {
          setError(e?.message ?? strings.autodialerAssignFailed);
        }
        setIsLoading(false);
        return;
      }

      if (isAutodialerHpFlow) {
        try {
          const outcome = await runInitializeAutodialerHpFlow();
          if (outcome === 'redirecting') {
            return;
          }
        } catch (e: any) {
          setError(e?.message ?? strings.autodialerHpLoadFailed);
        }
        setIsLoading(false);
        return;
      }

      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const assignedCaseResult = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
      if (assignedCaseResult.isSuccess) {
        await applyAssignedCase(assignedCaseResult.data);
      }
      setIsLoading(false);
    })();
  }, []);

  // Fetch active power hour config
  useEffect(() => {
    if (isAutodialerHpFlow) {
      setActivePowerHourConfig(null);
      return;
    }

    (async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const activePowerHourConfig = await reportingService.getActivePowerHourConfig(token.accessToken);
      setActivePowerHourConfig(activePowerHourConfig);
    })();
  }, [caseInfo, isAutodialerHpFlow]);

  const handleCaseAssignment = async (assignedCaseResult: Result<CaseInfo>) => {
    recording.reset();
    if (assignedCaseResult.isSuccess) {
      const assignedCase = assignedCaseResult.data;
      setCaseInfo(assignedCase);
      setInputCaseComments('');
      setLastPersistedComment('');
      setPreSalesContext({});
      if (assignedCase.bookingStatus === 'ReschedulingNeeded') {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const cancelledSlotResponse = await salesAppointmentsService.getLatestSlot(token.accessToken, assignedCase.opportunityId, salesChannel);
        if (cancelledSlotResponse.isSuccess) {
          setCancelledSlot(cancelledSlotResponse.data);
        } else {
          setCancelledSlot(null);
        }
      } else {
        setCancelledSlot(null);
      }
    } else if (assignedCaseResult.errors && assignedCaseResult.errors[0].includes('NO_CASE_AVAILABLE')) {
      const errorDeserialized = JSON.parse(assignedCaseResult.errors[0]);
      const errorDate = dayjs(errorDeserialized.RetryIn).format('YYYY-MM-DDTHH:mm:ss');
      // Get the current UTC time
      const nowUtc = dayjs().utc().format('YYYY-MM-DDTHH:mm:ss');
      // Calculate the difference in milliseconds
      const differenceInSeconds = dayjs(errorDate).diff(nowUtc, 'milliseconds');
      setRetryCountdown(differenceInSeconds);
      setCaseInfo(null);
    } else {
      setCaseInfo(null);
      throw assignedCaseResult.errors;
    }
  };

  /**
   * Autodialer-HP booking succeeded (Zeeg video or field sales). Stop the recorder and upload the
   * recording tagged `Booked` immediately, without waiting for the close-case button.
   */
  const handleHpBooked = () => {
    recording.reportOutcome(CaseStatus.Booked);
  };

  /**
   * Autodialer-HP cancellation handler: fired when the presales form is submitted as a cancellation.
   * Stops the active recorder and uploads the recording tagged `ReachedButNotBooked`. If a booking
   * already stopped the recorder (reported as `Booked`), `reportOutcome` is a no-op, so a booked
   * call is never downgraded.
   */
  const handleHpCancelled = () => {
    recording.reportOutcome(CaseStatus.ReachedButNotBooked);
  };

  /**
   * Handles the online changing on the header
   * @param isOnline whether the online flag is true or false on the header
   */
  const onOnlineChanged = async (isOnline: boolean) => {
    await withGuard(async () => {
      try {
        recording.reset();
        setError(undefined);
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        // If the sales channel is DigitalSales and the user is not an admin, then set the sales channel to the role based sales channel
        // This is to ensure that the user can only book cases for their own sales channel
        if (isOnline) {
          const assignedCaseResult = await bookingPageService.getOrAssignPriorityCase(token.accessToken!, salesChannel!);
          handleCaseAssignment(assignedCaseResult);
        } else {
          if (inputCaseComments && inputCaseComments !== lastPersistedComment && caseInfo?.internalId) {
            await bookingPageService.addCaseComment(caseInfo.internalId, inputCaseComments, salesChannel, token.accessToken);
          }
          setInputCaseComments('');
          setLastPersistedComment('');
          recording.stopAndDiscard();
          setDisplayConfirmationPage(false);
          await bookingPageService.releaseCase(caseInfo?.internalId ?? '', salesChannel, token?.accessToken!);
          setCaseInfo(null);
        }
        setIsOnline(isOnline);
      } catch (e: any) {
        setError(e);
        throw e;
      }
    }, setIsLoading);
  };

  const onTryFetchNextCase = async () => {
    if (inboundCenter !== undefined || isDirectCaseFlow) {
      return;
    }

    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const assignedCaseResult = await bookingPageService.getOrAssignPriorityCase(token.accessToken!, salesChannel!);
      handleCaseAssignment(assignedCaseResult);
    } catch (e) {
      setError(e);
    }
  };

  const onRefreshCountdown = async () => {
    await withGuard(async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const updateCaseResult = await bookingPageService.updateCaseAssignment(caseInfo?.internalId!, salesChannel, token.accessToken!);
        if (updateCaseResult.isSuccess) {
          setCaseInfo({ ...caseInfo!, expireAt: updateCaseResult.data });
        }
      } catch (e) {
        setError(e);
      }
    });
  };

  const customizeUserInterface = (user: UserDataDto) => {
    if (user.selectedBookingButton) {
      setBookingButtonStyle(user.selectedBookingButton);
    }
  };

  const header = (
    <Header
      changeUserInterface={customizeUserInterface}
      hideNavigationMenu={isAutodialerHpFlow}
      optionalComponent={
        <HeaderBookingPage
          inboundCenter={inboundCenter}
          isAutodialerFlow={isDirectCaseFlow}
          isOnline={isOnline}
          caseId={caseInfo?.caseId!}
          salesChannel={salesChannel}
          caseExpiration={displayConfirmationPage || isAutodialerHpFlow ? undefined : caseInfo?.expireAt}
          onOnlineChanged={onOnlineChanged}
          onRefreshCountdown={onRefreshCountdown}
          isLoading={isLoading}
        />
      }
    />
  );

  const onBackToInbound = async (isSuccessful: boolean) => {
    await onOnlineChanged(false);
    navigate('/inbound', {
      state: {
        isSuccess: isSuccessful,
      },
    });
    setBookedSlot(undefined);
  };

  const confirmationPageOpenNextCase = async (caseComment: string | undefined) => {
    if (isAutodialerFlow) {
      await withGuard(async () => {
        await finishAutodialerSession(caseComment);
      }, setIsOpeningNextCase);
      return;
    }

    await withGuard(async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (caseComment && caseComment !== lastPersistedComment && caseInfo?.internalId) {
        await bookingPageService.addCaseComment(caseInfo.internalId, caseComment, salesChannel, token.accessToken);
      }
      setInputCaseComments('');
      setLastPersistedComment('');
      const nextCase = await bookingPageService.getOrAssignPriorityCase(token.accessToken, salesChannel);

      // send recording for booked cases
      if (userShouldBeRecorded) {
        recording.reportOutcome(CaseStatus.Booked);
      }

      await wait(1);

      handleCaseAssignment(nextCase);

      setDisplayConfirmationPage(false);
    }, setIsOpeningNextCase);
  };

  if (error) {
    return (
      <>
        {header}
        <div className="container">
          <Alert type="error" message={strings.errorOccurred} description={error?.message ?? error}></Alert>
        </div>
      </>
    );
  }

  if (conflictOldCase) {
    return (
      <>
        {header}
        <AssignmentConflictModal
          open
          oldCustomerName={`${conflictOldCase.firstName ?? ''} ${conflictOldCase.lastName ?? ''}`.trim()}
          onProceedWithOld={onConflictProceedWithOld}
          onChooseNew={onConflictChooseNew}
        />
      </>
    );
  }

  const openErrorNotification = (isCaseNotFound: boolean) => {
    notification.open({
      key: 'errorNotification',
      message: strings.errorOccurred,
      description: (
        <>
          <p>{isCaseNotFound ? strings.caseNotFoundErrorDescription : strings.genericCloseCaseErrorDescription}</p>
          <p>{strings.youCanGetANewCase}</p>
          <Button
            type="primary"
            onClick={async (_) => {
              setIsLoading(true);
              try {
                notification.destroy('errorNotification');
                await confirmationPageOpenNextCase(undefined);
                await wait(0.5);
              } catch (e: any) {
                setCaseInfo(null);
                setError(e);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            {strings.getNewCase}
          </Button>
        </>
      ),
      placement: 'topRight',
      duration: 0,
      type: 'error',
    });
  };

  const handleCaseClosed = async (caseComplete: OpportunityComplete, bookedSlot?: Slot) => {
    await withGuard(async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      // Always persist manual comment BEFORE closing the case if present.
      if (inputCaseComments && caseInfo?.internalId) {
        await bookingPageService.addCaseComment(caseInfo.internalId, inputCaseComments, salesChannel, token.accessToken);
        setLastPersistedComment(inputCaseComments);
      }

      caseComplete.callSource = inboundCenter ? 'Inbound' : isAutodialerFlow ? 'Autodialer' : 'Queue';
      const closeCaseResult = await bookingPageService.closeCase(caseComplete, token.accessToken);

      if (!closeCaseResult.isSuccess) {
        const isCaseNotFound = closeCaseResult.errors?.includes('CASE_NOT_FOUND');
        openErrorNotification(isCaseNotFound!);
        setCaseInfo(null);
        throw new Error(closeCaseResult.errors?.join(', ') || 'Failed to close case');
      }

      if (caseComplete.caseStatus === CaseStatus.Booked) {
        //If no booked slot is provided, then there was an error with the booking. Open the error notification that allows the user to get a new assignment case
        if (!bookedSlot) {
          openErrorNotification(true);
          return;
        }

        fireBookingConfetti();

        setBookedSlot(bookedSlot);
        setDisplayConfirmationPage(true);

        // In the autodialer flow, upload the recording as soon as the booking is done rather than
        // waiting for the agent to click through the confirmation page (finishAutodialerSession).
        // reportOutcome consumes the active recorder, so the later call is a harmless no-op.
        if (isAutodialerFlow && userShouldBeRecorded) {
          recording.reportOutcome(CaseStatus.Booked);
        }
      } else {
        setInputCaseComments('');
        setLastPersistedComment('');
        if (userShouldBeRecorded) {
          recording.reportOutcome(caseComplete.caseStatus);
        }
        await wait(1);

        if (isAutodialerFlow) {
          await goToBookingOffline();
        } else if (inboundCenter) {
          await onBackToInbound(closeCaseResult.isSuccess);
        } else {
          const nextCase = await bookingPageService.getOrAssignPriorityCase(token.accessToken, salesChannel);
          handleCaseAssignment(nextCase);
          if (nextCase.isSuccess) {
            notification.open({
              message: strings.caseCompleteNotification,
              placement: 'topRight',
              type: 'success',
            });
          } else {
            notification.open({
              message: strings.assignNewCaseError,
              placement: 'topRight',
              type: 'error',
            });
          }
        }
      }
    });
  };

  if (assignmentChanged) {
    return <AssignmentChangedModal open onRefresh={() => window.location.reload()} />;
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <div className="loading-text" style={{ marginTop: '2vh' }}>
          Loading...
        </div>
      </div>
    );
  }

  let scripts;

  if (caseInfo) {
    scripts = <CaseScript salesScript={caseInfo.salesScript} salesObjections={caseInfo.salesObjections} resetPosition={!displayConfirmationPage} />;
  }

  if (displayConfirmationPage) {
    return (
      <>
        {header}
        <div className="bp-top-microphone">
          <OnlineRecordingBookingPage caseInfo={caseInfo} isAgentOnline={isOnline} isUserRecorded={userShouldBeRecorded} isRecording={recording.isRecording} />
        </div>
        <div className="bp-layout">
          <div className="bp-main">
            <ConfirmationPage
              inboundCenter={inboundCenter}
              isAutodialerFlow={isDirectCaseFlow}
              onBackToInbound={onBackToInbound}
              caseInfo={caseInfo!}
              bookedSlot={bookedSlot!}
              cancelledSlot={cancelledSlot}
              inputCaseComments={inputCaseComments}
              setInputCaseComments={setInputCaseComments}
              onOpenNextCase={confirmationPageOpenNextCase}
              isOpeningNextCase={isOpeningNextCase}
              handleCallButtonClick={handleCallClick}
            />
          </div>
          {!hideScriptsAndObjections && <div className="bp-sider">{scripts}</div>}
        </div>
      </>
    );
  }

  if (isOnline && caseInfo) {
    return (
      <>
        {header}
        <div className={`bp-vertical-allignment${isAutodialerHpFlow ? ' bp-vertical-allignment--presales' : ''}`}>
          {!isAutodialerHpFlow && activePowerHourConfig && <PowerHourBar powerHourConfig={activePowerHourConfig} />}
          {/* Recording indicator is shown for every recorded flow, including autodialer-HP. */}
          <div className="bp-top-microphone">
            <OnlineRecordingBookingPage caseInfo={caseInfo} isAgentOnline={isOnline} isUserRecorded={userShouldBeRecorded} isRecording={recording.isRecording} />
          </div>
          <div className={`bp-layout${isAutodialerHpFlow ? ' bp-layout--presales' : ''}`}>
            <div className="bp-main">
              <OnlineBookingPage
                inboundCenter={inboundCenter}
                inboundAutodialed={inboundAutodialed}
                isAutodialerFlow={isDirectCaseFlow}
                isAutodialerHpFlow={isAutodialerHpFlow}
                preSalesContext={preSalesContext}
                preSalesQuestionnaire={preSalesQuestionnaire}
                caseInfo={caseInfo}
                setCaseInfo={setCaseInfo}
                cancelledSlot={cancelledSlot}
                onCaseClosed={handleCaseClosed}
                inputCaseComments={inputCaseComments}
                setInputCaseComments={setInputCaseComments}
                bookingButtonStyle={bookingButtonStyle}
                handleCallButtonClick={handleCallClick}
                onHpBooked={handleHpBooked}
                onHpCancelled={handleHpCancelled}
              />
            </div>
            {!hideScriptsAndObjections && <div className="bp-sider">{scripts}</div>}
          </div>
        </div>
      </>
    );
  }

  if (isOnline && caseInfo === null && isLoading === false) {
    return (
      <>
        {header}
        <OnlineBookingNoCasesPage onTryFetchNextCase={onTryFetchNextCase} retryCountdown={retryCountdown!} />
      </>
    );
  }

  if (!isOnline && !isDirectCaseFlow) {
    return (
      <>
        {header}
        <OfflineBookingPage />
      </>
    );
  }

  return (
    <>
      {header}
      <Alert type="error" message="The application reached an unexpected state, please contact support." />
    </>
  );
};
